import { useEffect } from "react";
import { Button } from "@electro-planner/ui";
import { RELEASES } from "../releases.ts";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Modal med alla releasenoter, nyaste överst. Stängs via Esc, backdrop-klick
 * eller knapp. Auto-open-logiken (localStorage-jämförelse) ligger i Canvas.
 */
export function ReleaseNotesDialog({ open, onOpenChange }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-[10vh]"
      onClick={() => onOpenChange(false)}
      role="presentation"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-lg border border-[var(--color-border)] bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Releasenoter"
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-fg)]">
              Nyheter i Electro Planner
            </h2>
            <p className="text-xs text-[var(--color-muted)]">
              Senaste ändringarna per version.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            aria-label="Stäng"
          >
            Stäng
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
          <ul className="flex flex-col gap-4">
            {RELEASES.map((r) => (
              <li key={r.version} className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-[var(--color-fg)]">
                    v{r.version}
                    {r.title ? (
                      <span className="ml-2 font-normal text-[var(--color-muted)]">
                        {r.title}
                      </span>
                    ) : null}
                  </h3>
                  <time className="text-xs text-[var(--color-muted)]" dateTime={r.date}>
                    {r.date}
                  </time>
                </div>
                <ul className="ml-4 list-disc flex flex-col gap-1 text-sm text-[var(--color-fg)]">
                  {r.items.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
